// ===========================================================================
// ReHome — Emlékeztetők adatrétege
//
// Két, egymástól eltérő igényű nézet szolgál ki ugyanabból a táblából:
//
//   Lista  — a soron következő emlékeztetők esedékesség szerint, KURZOROSAN
//            lapozva. Egy nagy menhelynél (állatok x ismétlődő események)
//            ez több ezer sor is lehet; az eddigi `limit(2000)` csendben
//            levágta a végét, ami rosszabb a lapozásnál: a levágott
//            emlékeztetőkről senki nem tudott.
//
//   Naptár — CSAK a látható rács napjai. Egy hónap váltása így fix méretű
//            kérés marad akkor is, ha a menhelynek tízezer emlékeztetője van.
//
// Az `animal_medical_events` RLS-e menhelyre szűkít (animal_medical_events_
// staff_all), ezért itt — az utókövetéssel ellentétben — nem kell külön
// jogosultsági kaput építeni.
// ===========================================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, isSupabaseConfigured } from './supabase';
import { toCamelCase } from './hooks';
import type { AnimalMedicalReminder, MedicalEventType } from './types';

export const REMINDER_PAGE_SIZE = 25;

/**
 * Az emlékeztető-sorhoz tartozó állat mezői.
 *
 * A képmezők azért kellenek, mert a naptár hónapnézete az állatok apró
 * portréit mutatja — és mivel a képek alapértelmezés szerint privátok, a
 * megjelenítéshez a `useAnimalCoverUrls` kötegelt aláírása kell.
 */
const REMINDER_SELECT =
  '*, animal:animals!inner(id, name, species, image_url, gallery, private_gallery, shelter_id)';

const isDev = () => {
  try { return (import.meta as any).env?.DEV; } catch { return false; }
};

export interface ReminderFilters {
  animalId?: string | null;
  eventType?: MedicalEventType | null;
  onlyOverdue?: boolean;
}

const applyFilters = (builder: any, filters: ReminderFilters) => {
  let q = builder;
  if (filters.animalId) q = q.eq('animal_id', filters.animalId);
  if (filters.eventType) q = q.eq('event_type', filters.eventType);
  if (filters.onlyOverdue) q = q.lt('next_due_date', new Date().toISOString().split('T')[0]);
  return q;
};

// ═══════════════════════════════════════════════════════════════════════════
// Lista nézet — kurzoros lapozás
// ═══════════════════════════════════════════════════════════════════════════

export interface ReminderListState {
  reminders: AnimalMedicalReminder[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  loadMore: () => void;
  reload: () => void;
}

/**
 * Az emlékeztetők listája esedékesség szerint növekvő sorrendben, kurzorosan.
 *
 * A kurzor az utolsó látott sor `(next_due_date, id)` párja. A PostgREST-nek
 * nincs sorérték-összehasonlítása, ezért a feltétel kézzel bontva:
 * `dátum > D VAGY (dátum = D ÉS id > I)`. Az `id` a döntetlent bontja — enélkül
 * egy napra eső több emlékeztetőnél a lapozás vagy kihagyna, vagy ismételne
 * sorokat.
 *
 * A párhuzamos kérés és az elavult válasz kezelése azonos az utókövetésnél
 * használt mintával (l. followup.ts).
 */
export function useShelterReminderPage(
  shelterId: string | undefined,
  filters: ReminderFilters,
  pageSize: number = REMINDER_PAGE_SIZE
): ReminderListState {
  const [reminders, setReminders] = useState<AnimalMedicalReminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inFlight = useRef(false);
  const generation = useRef(0);
  const cursor = useRef<{ date: string; id: string } | null>(null);

  const { animalId, eventType, onlyOverdue } = filters;

  const fetchPage = useCallback(async (reset: boolean) => {
    if (!shelterId || !isSupabaseConfigured()) {
      setReminders([]); setLoading(false); setHasMore(false); return;
    }
    if (!reset && inFlight.current) return;

    if (reset) {
      generation.current += 1;
      cursor.current = null;
      setLoading(true);
      setReminders([]);
    } else {
      setLoadingMore(true);
    }

    const myGeneration = generation.current;
    inFlight.current = true;

    try {
      let q = supabase
        .from('animal_medical_events')
        .select(REMINDER_SELECT)
        .eq('animal.shelter_id', shelterId)
        .eq('is_recurring', true)
        .not('next_due_date', 'is', null);

      q = applyFilters(q, { animalId, eventType, onlyOverdue });

      const c = cursor.current;
      if (c) {
        q = q.or(`next_due_date.gt.${c.date},and(next_due_date.eq.${c.date},id.gt.${c.id})`);
      }

      const { data, error: err } = await q
        .order('next_due_date', { ascending: true })
        .order('id', { ascending: true })
        .limit(pageSize);

      if (err) throw err;
      if (myGeneration !== generation.current) return;

      const page = toCamelCase<AnimalMedicalReminder[]>(data || []);
      const last = page[page.length - 1];
      if (last?.nextDueDate) cursor.current = { date: last.nextDueDate, id: last.id };

      setHasMore(page.length === pageSize);
      setReminders(prev => {
        if (reset) return page;
        const seen = new Set(prev.map(r => r.id));
        return [...prev, ...page.filter(r => !seen.has(r.id))];
      });
      setError(null);
    } catch (e: any) {
      if (myGeneration !== generation.current) return;
      if (isDev()) console.warn('[useShelterReminderPage]', e.message);
      setError(e.message);
      setHasMore(false);
    } finally {
      if (myGeneration === generation.current) {
        inFlight.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [shelterId, animalId, eventType, onlyOverdue, pageSize]);

  useEffect(() => { fetchPage(true); }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (!inFlight.current && hasMore) fetchPage(false);
  }, [fetchPage, hasMore]);

  const reload = useCallback(() => { fetchPage(true); }, [fetchPage]);

  return { reminders, loading, loadingMore, hasMore, error, loadMore, reload };
}

// ═══════════════════════════════════════════════════════════════════════════
// Naptár nézet — csak a látható tartomány
// ═══════════════════════════════════════════════════════════════════════════

/**
 * A megadott dátumtartományba eső emlékeztetők.
 *
 * A tartomány a LÁTHATÓ rácsé, nem a hónapé: a hónapnézet a szomszédos
 * hónapok kilógó napjait is mutatja, és azok sem maradhatnak üresen.
 *
 * A felső korlát azért van, hogy egy elgépelt vagy szélsőséges tartomány se
 * tudjon korlátlan választ kérni; egy hét vagy egy hónap ennél nagyságrendekkel
 * kevesebb sort ad.
 */
export function useShelterRemindersInRange(
  shelterId: string | undefined,
  from: string,
  to: string,
  filters: ReminderFilters,
  maxRows: number = 500
): { reminders: AnimalMedicalReminder[]; loading: boolean; truncated: boolean; reload: () => void } {
  const [reminders, setReminders] = useState<AnimalMedicalReminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [truncated, setTruncated] = useState(false);

  const generation = useRef(0);
  const { animalId, eventType, onlyOverdue } = filters;

  const fetchRange = useCallback(async () => {
    if (!shelterId || !from || !to || !isSupabaseConfigured()) {
      setReminders([]); setLoading(false); return;
    }
    generation.current += 1;
    const myGeneration = generation.current;
    setLoading(true);

    try {
      let q = supabase
        .from('animal_medical_events')
        .select(REMINDER_SELECT)
        .eq('animal.shelter_id', shelterId)
        .eq('is_recurring', true)
        .gte('next_due_date', from)
        .lte('next_due_date', to);

      q = applyFilters(q, { animalId, eventType, onlyOverdue });

      const { data, error } = await q
        .order('next_due_date', { ascending: true })
        .order('id', { ascending: true })
        .limit(maxRows);

      if (error) throw error;
      if (myGeneration !== generation.current) return;

      const rows = toCamelCase<AnimalMedicalReminder[]>(data || []);
      setReminders(rows);
      setTruncated(rows.length === maxRows);
    } catch (e: any) {
      if (myGeneration !== generation.current) return;
      if (isDev()) console.warn('[useShelterRemindersInRange]', e.message);
      setReminders([]);
    } finally {
      if (myGeneration === generation.current) setLoading(false);
    }
  }, [shelterId, from, to, animalId, eventType, onlyOverdue, maxRows]);

  useEffect(() => { fetchRange(); }, [fetchRange]);

  return { reminders, loading, truncated, reload: fetchRange };
}

// ═══════════════════════════════════════════════════════════════════════════
// Összegzés és állatválasztó
// ═══════════════════════════════════════════════════════════════════════════

export interface ReminderCounts {
  total: number;
  dueSoon: number;
  overdue: number;
}

export const MEDICAL_DUE_SOON_DAYS = 14;

/**
 * A három összegző szám — CSAK a darabszámok, sorok letöltése nélkül.
 *
 * A lista már nem hozza le az összes emlékeztetőt, tehát ezeket sem lehet
 * belőle összeszámolni. A `head: true` kérés a sorokat nem is küldi el, csak
 * a `Content-Range` fejlécből visszaadott darabszámot.
 */
export function useShelterReminderCounts(shelterId: string | undefined): {
  counts: ReminderCounts | null;
  loading: boolean;
  refetch: () => void;
} {
  const [counts, setCounts] = useState<ReminderCounts | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchCounts = useCallback(async () => {
    if (!shelterId || !isSupabaseConfigured()) { setCounts(null); setLoading(false); return; }
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const soon = new Date();
      soon.setDate(soon.getDate() + MEDICAL_DUE_SOON_DAYS);
      const soonStr = soon.toISOString().split('T')[0];

      const base = () => supabase
        .from('animal_medical_events')
        .select('id, animal:animals!inner(shelter_id)', { count: 'exact', head: true })
        .eq('animal.shelter_id', shelterId)
        .eq('is_recurring', true)
        .not('next_due_date', 'is', null);

      const [all, dueSoon, overdue] = await Promise.all([
        base(),
        base().gte('next_due_date', today).lte('next_due_date', soonStr),
        base().lt('next_due_date', today),
      ]);

      setCounts({
        total: all.count ?? 0,
        dueSoon: dueSoon.count ?? 0,
        overdue: overdue.count ?? 0,
      });
    } catch (e: any) {
      if (isDev()) console.warn('[useShelterReminderCounts]', e.message);
      setCounts(null);
    } finally {
      setLoading(false);
    }
  }, [shelterId]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  return { counts, loading, refetch: fetchCounts };
}

export interface AnimalOption {
  id: string;
  name: string;
  species: string | null;
}

/**
 * A menhely állatai a kereshető választóhoz — csak azonosító, név, faj.
 *
 * Szándékosan minden állat, nem csak azok, akiknek most van emlékeztetőjük:
 * a szűrő így akkor is használható, ha a kérdés éppen az, hogy "ennek az
 * állatnak van-e beütemezve valami". Három oszlop miatt a válasz akkor is
 * apró marad, ha a menhelynek több ezer állata van.
 */
export function useShelterAnimalOptions(shelterId: string | undefined): {
  options: AnimalOption[];
  loading: boolean;
} {
  const [options, setOptions] = useState<AnimalOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!shelterId || !isSupabaseConfigured()) { setOptions([]); setLoading(false); return; }

    setLoading(true);
    supabase
      .from('animals')
      .select('id, name, species')
      .eq('shelter_id', shelterId)
      .order('name', { ascending: true })
      .limit(5000)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          if (isDev()) console.warn('[useShelterAnimalOptions]', error.message);
          setOptions([]);
        } else {
          setOptions((data || []) as AnimalOption[]);
        }
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [shelterId]);

  return { options, loading };
}
